import { useTheme } from '@emotion/react';
import React from 'react';

import Accordion from '~/common/components/Accordions/Accordion';
import AccordionButton from '~/common/components/Accordions/AccordionButton';
import AccordionIcon from '~/common/components/Accordions/AccordionIcon';
import AccordionItem from '~/common/components/Accordions/AccordionItem';
import AccordionPanel from '~/common/components/Accordions/AccordionPanel';
import Alert from '~/common/components/Alerts/Alert';
import Box from '~/common/components/Box';
import SimpleGrid from '~/common/components/Grids/Grid/SimpleGrid';
import HStack from '~/common/components/Stacks/HStack';
import VStack from '~/common/components/Stacks/VStack';
import Text from '~/common/components/Texts/Text';
import Tooltip from '~/common/components/Tooltip';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import Divider from '~/common/mui/Divider';
import { formatCurrency } from '~/common/util/format';

import language from '../language.json';
import RentalCalculationReviewBreakdown from '../model/RentalCalculationReviewBreakdown';

type FormulaLine = {
  Name: string | string[];
  Value: string[];
  SortOrder?: number;
};

type FormulaData = {
  BreakDown: FormulaLine[];
  addBacksList: string[];
};

type Colors = ReturnType<typeof useTheme>['colors'];

const renderFormulaLine = (
  formula: FormulaLine,
  addBacks: string[],
  colors: Colors,
  key?: number,
) => {
  switch (formula.Name) {
    case 'Gross Rents Equation':
      return (
        <Box sx={{ alignItems: 'start' }} key={key}>
          <Text fontSize="14px" fontWeight="700">
            {formula.Name + ':'}
          </Text>
          <Box>
            {formula.Value.map((val) => {
              if (val === 'Addbacks') {
                return (
                  <Tooltip
                    hasArrow
                    placement="top"
                    label={`${addBacks.join(', ')}`}
                    color={colors?.black}
                    bg={colors?.gray[200]}
                    key={val}
                  >
                    <Text as="span" color={colors?.blue[500]}>
                      {val}
                    </Text>
                  </Tooltip>
                );
              } else {
                return (
                  <Text as="span" key={val}>
                    {val}
                  </Text>
                );
              }
            })}
          </Box>
        </Box>
      );
    default:
      return (
        <VStack sx={{ alignItems: 'left' }} key={key}>
          <Text fontSize="14px">
            {Array.isArray(formula.Name) ? (
              <>
                <Text as="span" fontWeight="700">
                  {formula.Name[0]}
                </Text>
                {formula.Name.length > 1 && (
                  <Text as="span" fontWeight="400">
                    {': '}
                    {formula.Name.slice(1).map((item, i) => (
                      <Text
                        key={`name-part-${(formula.Name as string[])[0]}-${item}`}
                        as="span"
                        fontWeight={
                          i === 0 && !item.trim().startsWith('(')
                            ? '700'
                            : '400'
                        }
                        style={{
                          marginRight:
                            i < (formula.Name as string[]).length - 2
                              ? '1.25rem'
                              : undefined,
                        }}
                      >
                        {item}
                      </Text>
                    ))}
                  </Text>
                )}
              </>
            ) : (
              <Text as="span" fontWeight="700">
                {formula.Name + ':'}
              </Text>
            )}
          </Text>
          {formula.Value.map((val) => {
            if (val === 'LineBreak')
              return (
                <Divider
                  orientation="horizontal"
                  sx={{ width: '190px', borderColor: 'black' }}
                  key={val}
                />
              );
            return (
              <Text key={val} fontSize="14px" fontWeight="400">
                {val}
              </Text>
            );
          })}
        </VStack>
      );
  }
};

const renderYearContainer = (
  title: number,
  breakdown: FormulaLine[],
  addBacksList: string[],
  colors: Colors,
  hideAccordion: boolean,
) => {
  const Container = (hideAccordion ? Box : AccordionPanel) as React.ElementType;
  return (
    <Box border="1px solid" borderRadius={4} borderColor="gray.200" key={title}>
      <Box bgColor="gray.50" borderTopRadius={4} h="40px" p={2} px={4}>
        <Text fontWeight={500} fontSize="lg">
          {title < 1
            ? language.FHA_1040_FORM_HEADERS.MOST_RECENT_YEAR
            : language.FHA_1040_FORM_HEADERS.PREVIOUS_YEAR}
        </Text>
      </Box>
      <Container p={6} mt="2px" background={colors?.white}>
        <VStack sx={{ alignItems: 'left' }} spacing={4}>
          {breakdown?.map((formulaLine, key) =>
            renderFormulaLine(formulaLine, addBacksList, colors, key),
          )}
        </VStack>
      </Container>
    </Box>
  );
};

const renderFHA1040Breakdown = (
  formula: FormulaData,
  colors: Colors,
  hideAccordion: boolean,
) => {
  const breakdownCopy = [...formula.BreakDown];
  const topOfLine = breakdownCopy.splice(0, 1)[0];
  const startingPoint = breakdownCopy.findIndex(
    (breakdown) => breakdown.SortOrder === 1,
  );
  const endPoint = breakdownCopy.findIndex(
    (breakdown) => breakdown.SortOrder === 4,
  );
  const yearBreakdowns: FormulaLine[][] = [
    breakdownCopy.splice(startingPoint, endPoint + 1),
  ];
  if (breakdownCopy[startingPoint]?.SortOrder === 1) {
    yearBreakdowns.push(breakdownCopy.splice(startingPoint, endPoint + 1));
  }
  const restOfFormula = breakdownCopy;

  return (
    <VStack sx={{ alignItems: 'left' }} spacing={4}>
      {renderFormulaLine(topOfLine, formula.addBacksList, colors)}
      <SimpleGrid columns={2} spacingX={8} spacingY={8} pb={4}>
        {yearBreakdowns.map((year, i) => {
          return renderYearContainer(
            i,
            year,
            formula.addBacksList,
            colors,
            hideAccordion,
          );
        })}
      </SimpleGrid>
      {restOfFormula.map((formulaLine, key) =>
        renderFormulaLine(formulaLine, formula.addBacksList, colors, key),
      )}
    </VStack>
  );
};

type Rental1040Props = {
  data?: RentalCalculationReviewBreakdown;
  pitia?: number;
  formulas?: FormulaData;
  isFHA1040?: boolean;
  isInCombinedCard?: boolean;
  hideAccordion?: boolean;
};

function Rental1040({
  data,
  pitia,
  formulas,
  isFHA1040,
  isInCombinedCard,
  hideAccordion,
}: Readonly<Rental1040Props>) {
  const { colors } = useTheme();
  const productTypeName = useGetLoanSummary().data.loanInfo.loanProductType;
  const isFHA = isFHA1040 ?? productTypeName === 'FHA';

  if (!data) {
    return null;
  }

  const twelveMonthHistoryTip =
    data.inputFields?.find(
      (b) => b?.fieldName === 'DisplayRentalTwelveMonthHistoryTip',
    )?.fieldValue === 'True';

  function Panel() {
    return (
      <>
        {!isFHA && (
          <VStack sx={{ alignItems: 'left' }} spacing={4}>
            {formulas?.BreakDown?.map((formula, key) =>
              renderFormulaLine(formula, formulas.addBacksList, colors, key),
            )}
          </VStack>
        )}
        {isFHA &&
          formulas &&
          renderFHA1040Breakdown(formulas, colors, hideAccordion ?? false)}
      </>
    );
  }

  return (
    <Box>
      {hideAccordion && (
        <Box pl="1rem">
          <Panel />
        </Box>
      )}
      {!hideAccordion && (
        <Accordion
          defaultIndex={[0]}
          allowMultiple
          mt={isInCombinedCard ? 4 : 8}
        >
          <AccordionItem
            background={colors?.gray[50]}
            border={`1px solid ${colors?.gray[200]}`}
          >
            <AccordionButton>
              <Box
                flex="1"
                textAlign="left"
                as="h3"
                fontSize="18px"
                fontWeight="500"
              >
                1040 - Schedule E Review Breakdown
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel p={6} mt="2px" background={colors?.white}>
              <Panel />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
      {twelveMonthHistoryTip && (
        <Alert status="info" mt={4}>
          {language.FNMA_GUIDELINES}
        </Alert>
      )}
      {!isInCombinedCard && (
        <Box
          bg={colors?.bgGray[100]}
          padding={{ sm: '20px', md: '20px' }}
          mt={8}
        >
          <Text fontSize="16px" fontWeight="500">
            1040 - Schedule E:
          </Text>
          <HStack>
            <Text fontSize="14px" fontWeight="500">
              Adjusted PITIA:
            </Text>
            <Text fontSize="14px" fontWeight="400">
              {!!pitia && `${formatCurrency(pitia)} to `}
              {formatCurrency(data.getAdjustedPITIA())}
            </Text>
          </HStack>
          <HStack>
            <Text fontSize="14px" fontWeight="500">
              Positive Net Rental Income:
            </Text>
            <Text fontSize="14px" fontWeight="400">
              {formatCurrency(data.getPositiveNetRentalIncome())}
            </Text>
          </HStack>
        </Box>
      )}
    </Box>
  );
}

export default Rental1040;
